/**
 * [INPUT]: 依赖 ./site 的 SITE / absoluteUrl；FAQ 条目由调用方传入
 * [OUTPUT]: 对外提供 buildHomeJsonLd / buildOrganizationJsonLd 等 JSON-LD 构造函数
 * [POS]: apps/web/seo 的结构化数据构建层，被 Seo 组件注入 <script type="application/ld+json">
 * [PROTOCOL]: 变更时更新本头部，然后检查 ../CLAUDE.md
 */
import { SITE, absoluteUrl } from './site'

export interface FaqItem {
  q: string
  a: string
}

export function buildOrganizationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE.name,
    url: absoluteUrl('/'),
    logo: absoluteUrl('/logo-mark.svg'),
    email: SITE.contactEmail,
  }
}

export function buildWebSiteJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE.name,
    url: absoluteUrl('/'),
    description: SITE.defaultDescription,
    inLanguage: 'en',
    publisher: {
      '@type': 'Organization',
      name: SITE.name,
      url: absoluteUrl('/'),
    },
  }
}

export function buildWebApplicationJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: SITE.name,
    url: absoluteUrl('/'),
    applicationCategory: 'MultimediaApplication',
    operatingSystem: 'Web Browser',
    browserRequirements: 'Requires JavaScript and a modern web browser',
    description: SITE.defaultDescription,
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    featureList: [
      'On-device PSL facial attractiveness scoring (1–8 scale)',
      'Geometric analysis: harmony, dimorphism, angularity, presentation',
      'Photo never leaves the device',
      'Optional AI expert diagnosis from anonymized metrics only',
    ],
  }
}

export function buildFaqPageJsonLd(items: FaqItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  }
}

/** Steps describing how a user gets their PSL Scale score — powers the HowItWorks section as HowTo schema. */
const HOW_TO_STEPS = [
  {
    name: 'Upload a front-facing photo',
    text: 'Upload a clear, eye-level front-facing portrait with even lighting and a natural expression. Gender is inferred automatically.',
  },
  {
    name: 'On-device facial analysis',
    text: 'Your photo is analyzed entirely in your browser — facial geometry and proportions are measured locally in milliseconds, with no server round-trip and no upload.',
  },
  {
    name: 'Geometric feature extraction',
    text: 'From your facial geometry we compute dimensionless ratios and angles: facial thirds, midface ratio, interpupillary distance, canthal tilt, gonial angle, and more.',
  },
  {
    name: 'Get your PSL Scale score and tier',
    text: 'Measured values map to the 0–8 PSL Scale with a tier, a four-dimension breakdown, strengths, weaknesses, and natural improvement suggestions.',
  },
]

export function buildHowToJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to test your PSL Scale score with PSL Analyzer',
    description:
      'Get your free PSL Scale score (0–8) entirely on-device in under a minute. No photo upload to any server.',
    inLanguage: 'en',
    estimatedCost: { '@type': 'MonetaryAmount', currency: 'USD', value: '0' },
    tool: [
      { '@type': 'HowToTool', name: 'A modern web browser' },
      { '@type': 'HowToTool', name: 'One front-facing photo' },
    ],
    step: HOW_TO_STEPS.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  }
}

export function buildHomeJsonLd(faqItems: FaqItem[]) {
  return [
    buildOrganizationJsonLd(),
    buildWebSiteJsonLd(),
    buildWebApplicationJsonLd(),
    buildHowToJsonLd(),
    buildFaqPageJsonLd(faqItems),
  ]
}
